import { Injectable, Injector } from "@angular/core";
import { DataService } from "../data.service";
import { Observable } from "rxjs/Observable";
import { ParentEventFromSearch } from "../../admin/events-screen/event-page-models/parent-event-from-search";
import { ChildEvent } from "../../admin/events-screen/event-page-models/child-event";
import { ParentEventMessage } from "../../admin/events-screen/event-page-models/parent-event-message";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { EventEditor } from "../../admin/events-screen/event-page-models/event-editor";
import {
  ParentQuestion,
  ParentQuestionType,
} from "../../admin/events-screen/event-page-models/parent-question";
import {
  Circle,
  County,
  Layer,
  Nest,
} from "../../admin/events-screen/event-page-models/noal.models";
import { observableToPromise } from "../../admin/events-screen/utils/observableToPromise";
import { HttpWrapperService } from "../http.wrapper.service";
import { SessionManagerService } from "../SessionManager/session-manager.service";
import { EventPageFeatures } from "../../admin/events-screen/event-page-models/event-page-features";
import { EventStatus } from "../../admin/events-screen/event-page-models/event-status";
import { HttpErrorResponse } from "@angular/common/http";
import { AlertService } from "../alert.service";
import { Router } from "@angular/router";
import { EventFormInitialData } from "../../admin/events-screen/event-page-models/event-form-initial-data";
import {
  EventSerializerService,
  ParentEventDataFromTheServer,
} from "./eventSerializer.service";
import { PostEventModel } from "../../admin/events-screen/event-page-models/post-event-model";
import { SaveActionResult } from "../../admin/events-screen/event-page-models/save-action-result";
import { EventFormStatusEnum } from "../../admin/events-screen/event-page-models/event-form-status.enum";
import { PutEventModel } from "../../admin/events-screen/event-page-models/put-event-model";
import { EventFormDynamicField } from "../../admin/events-screen/event-page-models/event-form-dynamic-field";
import { InternalEventFormModel } from "../../admin/events-screen/event-page-models/internal-event-form-model";
import { EventValidationService } from "./eventValidation.service";
import { EventFormEventType } from "../../admin/events-screen/event-page-models/event-form-event-type";
import { EventCollectionItem } from "../../admin/events-screen/event-page-models/event-collection-item";
import { AppUserModel } from "../../admin/users/user-management-models/app-user.model";
import { UserManagementService } from "../user-management.service";
import { BudgetSection } from "../../admin/events-screen/event-page-models/budget-section";
import { EventOrgan } from "../../admin/events-screen/create-parent-event-form-pricing-nests/create-parent-event-form-pricing-nests.component";

// tslint:disable-next-line:interface-name
interface AllEvnetData {
  parentEvents: ParentEventFromSearch[];
  childEvents: ChildEvent[];
  eventEditors: EventEditor[];
  parentQuestions: ParentQuestion[];
}

export interface NoalGlobalData {
  counties: County[];
  layers: Layer[];
  nests: Nest[];
  circles: Circle[];
  ken_collections: Array<{
    id: number;
    kenim: number[];
    name: string;
    is_kehila: boolean;
  }>;
}

export interface ParentEventSearchState {
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
}

const eventDemoDataKeyForLS = "eventDemoDataKeyForLS_v7";

@Injectable()
export class EventsService {
  public static readonly EventPageFeatures: EventPageFeatures = {
    showOnlyYearInAdvancedSearch: true,
    noOpenCloseActionsFromSearchScreen: true,
    onlyOnePriceChange: true,
  };
  public readonly parentEventMessage$: BehaviorSubject<ParentEventMessage | null> =
    new BehaviorSubject<ParentEventMessage | null>(null);
  public readonly allParentEvents$: BehaviorSubject<ParentEventFromSearch[]> =
    new BehaviorSubject<ParentEventFromSearch[]>([]);
  public readonly eventFormInitialData$: BehaviorSubject<EventFormInitialData> =
    new BehaviorSubject<EventFormInitialData>(null);
  public readonly parentEventsSearchState$: BehaviorSubject<ParentEventSearchState> =
    new BehaviorSubject<ParentEventSearchState>(null);
  public readonly allPermittedUsers$: BehaviorSubject<EventEditor[]> =
    new BehaviorSubject<EventEditor[]>([]);
  public readonly permittedUsersSearchResults$: BehaviorSubject<EventEditor[]> =
    new BehaviorSubject<EventEditor[]>([]);
  public readonly allQuestions$: BehaviorSubject<ParentQuestion[]> =
    new BehaviorSubject<ParentQuestion[]>([]);
  private readonly eventFormDynamicFields$: BehaviorSubject<
    EventFormDynamicField[]
  > = new BehaviorSubject<EventFormDynamicField[]>([]);
  private readonly eventFormOrgansList$: BehaviorSubject<EventOrgan[]> =
    new BehaviorSubject<EventOrgan[]>([]);

  private isRedirectedToLogin: boolean = false;
  private MAX_NUMBER_OF_PARENT_EVENTS_ON_SCROLL: 200;

  constructor(
    dataService: DataService,
    private http: HttpWrapperService,
    private sessionManager: SessionManagerService,
    private injector: Injector,
    private router: Router
  ) {}

  public static legacySerializeChildEventsFromServer(
    childEventsFromServer: any[]
  ) {
    return childEventsFromServer.map((ev) => {
      const childEvent: ChildEvent = {
        parentEventId: 0,
        childEventPrice: ev.price,
        circle: [],
        nests: [],
        layers: [],
        childEventPriceChange: [],
        parentEventCode: ev.parentEventId,
        childEventCode: ev.code,
        childEventId: ev.id,
        eventName: ev.name,
        // only one type
        eventType: ev.type,
        eventStartDate: "",
        eventStartHour: "",
        eventEndDate: "",
        eventEndHour: "",
        prices: ev.price,
        eventOpenTo: ev.openToDetails,
        registeredChildren: ev.registeredChildrenCount,

        eventStatus: ev.isOpenForRegistration
          ? EventStatus.Open
          : EventStatus.Closed,
        registrationStartDate: new Date(),
        registrationEndDate: new Date(ev.registrationEnd),
        closedToMembers: false,
        brotherDiscount: false,
        eventIndex: "",
        gainCenter1: "",
        gainCenter2: "",
        eventCollection: [],
      };
      return childEvent;
    });
  }

  public static serializeParentEventsFromServer(parentEventsFromServer: any[]) {
    const dateFormatFromSearch = (date: number) => {
      if (!date) {
        return undefined;
      }
      return EventSerializerService.EpocToDateAndTime(date).datePicker;
    };

    return parentEventsFromServer.map((parentEv) => {
      const parentEvent: ParentEventFromSearch = {
        parentEventCode: parentEv.code,
        parentEventId: parentEv.id,
        eventName: parentEv.name,
        // only one type
        eventType: parentEv.event_type,
        eventStartDate: "",
        eventStartHour: "",
        eventEndDate: "",
        eventEndHour: "",
        prices: parentEv.price_details,
        eventOpenTo: parentEv.open_to.details,
        registeredChildren: parentEv.registered_children_count,

        eventStatus: parentEv.is_open_for_registration
          ? EventStatus.Open
          : EventStatus.Closed,
        registrationStartDate: new Date(),
        registrationEndDate: new Date(
          dateFormatFromSearch(parentEv.registration_end_date)
        ),
        closedToMembers: false,
        brotherDiscount: false,
        eventIndex: "",
        gainCenter1: "",
        gainCenter2: "",
        childEvents: parentEv.child_events.map((child) => {
          const childEvent: ChildEvent = {
            parentEventId: parentEv.id,
            childEventPrice: child.price,
            circle: [],
            nests: [],
            layers: [],
            childEventPriceChange: [],
            parentEventCode: parentEv.code,
            childEventCode: child.code,
            childEventId: child.id,
            eventName: child.name,
            // can_be_deleted: chidl.can_be_deleted,
            // only one type
            eventType: parentEv.event_type,
            eventStartDate: "",
            eventStartHour: "",
            eventEndDate: "",
            eventEndHour: "",
            prices: child.price.toString(),
            eventOpenTo: child.open_to.details,
            registeredChildren: child.registered_children_count,

            eventStatus: child.is_open_for_registration
              ? EventStatus.Open
              : EventStatus.Closed,
            registrationStartDate: new Date(),
            registrationEndDate: new Date(
              dateFormatFromSearch(child.registration_end_date)
            ),
            closedToMembers: false,
            brotherDiscount: false,
            eventIndex: "",
            gainCenter1: "",
            gainCenter2: "",
            eventCollection: [],
          };
          return childEvent;
        }),
      };
      return parentEvent;
    });
  }

  public async searchBudgetLinesForEvent(
    search: string,
    displayAllSections: boolean
  ): Promise<BudgetSection[]> {
    const body = {};
    if (!displayAllSections) {
      const allNestsIds = (this.eventFormOrgansList$.value || [])
        .filter((o) => o.nestOrCounty === "nest")
        .map((o) => o.id);
      body["ken_ids"] = Array.from(new Set(allNestsIds));
    }

    const response = await observableToPromise(
      this.http.post(
        `/django-api/budget-item-line/get_authorize_budget_item_lines/?search=${search}`,
        { ...body }
      )
    );
    const sections = await response.json();

    return sections.map((section) => {
      const budgetSection: BudgetSection = {
        budgetSectionId: section.id,
        budgetSectionDescription: section.description || "ללא תיאור",
      };
      return budgetSection;
    });
  }

  public async getCreateEventFormInitialData() {
    const response = await observableToPromise(
      this.http.get("/django-api/events/create_form_initial_data/")
    );

    const eventsFormNoalData = await response.json();

    const serverParentEventData: ParentEventDataFromTheServer = {
      budget_item_line: {
        id: 0,
        description: "",
      },
      child_events: [],
      child_events_to_delete: [],
      code: "",
      description: "",
      direct_permission_users: undefined,
      event_end_date: 0,
      event_start_date: 0,
      has_brother_discount: false,
      id: 0,
      merkaz_revach_1: "",
      merkaz_revach_3: "",
      name: "",
      priority_index: "",
      registration_end_date: 0,
      registration_start_date: 0,
      tax_payers_only: false,
      questions: eventsFormNoalData.questions,
      event_collections: eventsFormNoalData.event_collections,
    };

    const allParentEventDataFromServer =
      EventSerializerService.serializeParentEventDataForEditAndCreate(
        serverParentEventData
      );
    const eventCollections: EventCollectionItem[] =
      allParentEventDataFromServer.event_collections.map((ec) => ({
        id: ec.id.toString(),
        name: ec.name,
        isSelected: ec.is_selected,
      }));

    // this observable keeps the server value
    this.eventFormInitialData$.next({
      formStatus: EventFormStatusEnum.New,
      eventFormNoalData: {
        ...eventsFormNoalData,
        eventCollection: eventCollections,
      },
      eventFormParentEventData: allParentEventDataFromServer,
    });
    this.allQuestions$.next(allParentEventDataFromServer.questions);
  }

  public httpCatch = (err: HttpErrorResponse) => {
    if (err && err.status === 401 && !this.isRedirectedToLogin) {
      this.isRedirectedToLogin = true;
      const message = "נותקת מהמערכת עקב חוסר פעילות. התחבר מחדש.";
      const alertService = this.injector.get(AlertService);

      if (
        this.sessionManager.getMinuetsFromLastLogin() <
        DataService.MINUTES_DISCONNECTED_MESSAGE_THRESHOLD
      ) {
        alertService.error(message, true);
      }
      setTimeout(() => this.router.navigate(["/login"]), 1000);
      return new Observable(() => () => true);
    }
    throw err;
  };

  public async checkIfUserLogged() {
    try {
      await observableToPromise(this.http.get(`apiV2/users/getSelfEntities`));
    } catch (err) {
      this.httpCatch(err);
    }
  }
  public async cloneExistingEventFormInitialData(eventCode: string) {
    return await this.getExistingEventFormInitialData(eventCode, true);
  }

  public async getExistingEventFormInitialData(
    eventCode: string,
    isClone?: boolean
  ) {
    const response = await observableToPromise(
      this.http.get(`/django-api/events/${eventCode}/update_form_initial_data/`)
    );

    const allUpdateDataFromServer = await response.json();
    const updateParentEventData = allUpdateDataFromServer.parent_event;

    updateParentEventData.questions = allUpdateDataFromServer.questions || [];
    let serializedEventDataFromServer = {
      ...EventSerializerService.serializeParentEventDataForEditAndCreate(
        updateParentEventData
      ),
    };

    if (isClone) {
      serializedEventDataFromServer =
        EventSerializerService.removeFieldsFromClonedEvent(
          serializedEventDataFromServer
        );
    }
    const eventCollections: EventCollectionItem[] =
      allUpdateDataFromServer.event_collections.map((ec) => ({
        id: ec.id.toString(),
        name: ec.name,
        isSelected: ec.is_selected,
      }));
    this.eventFormInitialData$.next({
      formStatus: isClone
        ? EventFormStatusEnum.Copy
        : EventFormStatusEnum.Update,
      eventFormNoalData: {
        kehilot: allUpdateDataFromServer.kehilot,
        grades: allUpdateDataFromServer.grades,
        registration_periods: allUpdateDataFromServer.registration_periods,
        event_types: allUpdateDataFromServer.event_types,
        ken_collections: allUpdateDataFromServer.ken_collections,
        eventCollection: eventCollections,
      },
      eventFormParentEventData: serializedEventDataFromServer,
    });
    this.allQuestions$.next(serializedEventDataFromServer.questions);

    this.allPermittedUsers$.next(
      (updateParentEventData.direct_permission_users || []).map((u) => ({
        id: u.id,
        userName: u.email,
        lastName: u.last_name,
        firstName: u.first_name,
        canEditEvent: true,
      }))
    );
    return this.eventFormInitialData$.value;
  }
  public async getChildEventsOfOneParentEvent(
    parentEventCode: string
  ): Promise<ChildEvent[]> {
    const parentEvent = this.allParentEvents$.value.find(
      (f) => f.parentEventCode === parentEventCode
    );

    const response = await observableToPromise(
      this.http.get(
        `apiV2/parentEvents/${parentEvent.parentEventId}/childEvents`
      )
    );
    const childEvents = response.json().results;
    return EventsService.legacySerializeChildEventsFromServer(
      childEvents || []
    );
  }

  public getAllParentEvents(): Observable<any> {
    return this.allParentEvents$.asObservable();
  }
  public getEventFormDynamicFields(): Observable<any> {
    return this.eventFormDynamicFields$.asObservable();
  }
  public getEventFormOrgansList(): Observable<any> {
    return this.eventFormOrgansList$.asObservable();
  }
  public updateEventFormData(formData: InternalEventFormModel) {
    const rules: EventFormDynamicField[] =
      EventValidationService.buildEventDynamicFieldsRules(formData);
    this.eventFormDynamicFields$.next(rules);
    const allOrgans =
      (formData &&
        formData.AllChildEvents &&
        formData.AllChildEvents.ActiveChildEvents.map((c) => c.organs)) ||
      [];
    const allOrgansFlat = [];
    allOrgans.forEach((organs) => {
      if (organs && organs.length > 0) {
        organs.forEach((o) => {
          if (o && o.id) {
            allOrgansFlat.push({
              id: o.id,
              name: o.name,
              nestOrCounty: o.nestOrCounty,
            });
          }
        });
      }
    });

    this.eventFormOrgansList$.next(allOrgansFlat as any);
  }
  public getParentEventsSearchState(): Observable<ParentEventSearchState> {
    return this.parentEventsSearchState$.asObservable();
  }

  public getAllPermittedUsers(): Observable<EventEditor[]> {
    return this.allPermittedUsers$.asObservable();
  }

  public getAllQuestions(): Observable<ParentQuestion[]> {
    return this.allQuestions$.asObservable();
  }

  public async saveNewEvent(
    newEventData: PostEventModel
  ): Promise<SaveActionResult> {
    try {
      const dataForServer =
        EventSerializerService.removeEmptyFieldsFromEventPost(newEventData);
      delete dataForServer.code;
      dataForServer.child_events.forEach((ce) => {
        delete ce.code;
      });
      delete dataForServer.child_events_to_delete;
      let creatNewUrl = `/django-api/events/`;
      if (
        EventValidationService.getEventType(newEventData) ===
        EventFormEventType.Michlol
      ) {
        creatNewUrl = `/django-api/events/create_mihlol/`;
      }

      const response = await observableToPromise(
        this.http.post(creatNewUrl, dataForServer)
      );

      const body = await response.json();
      if (body) {
        return { success: true, data: body };
      }
      return { success: false, error: body };
    } catch (err) {
      return { success: false, error: err };
    }
  }
  public async saveUpdateExistingEvent(
    newEventData: PutEventModel
  ): Promise<SaveActionResult> {
    try {
      const dataForServer =
        EventSerializerService.removeEmptyFieldsFromEventPost(newEventData);
      delete dataForServer.code;
      dataForServer.child_events.forEach((ce) => {
        delete ce.code;
      });
      const eventId = newEventData.id;
      delete newEventData.id;
      let updateUrl = `/django-api/events/${eventId}/`;
      if (
        EventValidationService.getEventType(newEventData) ===
        EventFormEventType.Michlol
      ) {
        updateUrl = `/django-api/events/${eventId}/update_mihlol/`;
      }
      const response = await observableToPromise(
        this.http.put(updateUrl, dataForServer)
      );

      const body = await response.json();
      if (body) {
        return { success: true, data: body };
      }
      return { success: false, error: body };
    } catch (err) {
      return { success: false, error: err };
    }
  }
  public setParentEventMessage(msg: ParentEventMessage) {
    this.parentEventMessage$.next(msg);
  }
  public async getCurrentYear() {
    try {
      const response = await observableToPromise(
        this.http.get("/django-api/global/")
      );
      const body = response.json();
      return body.shnat_peilut;
    } catch (e) {}
  }

  public async updateUserPermissions(sourceUser: EventEditor): Promise<void> {
    {
      const currenValue = this.allPermittedUsers$
        .getValue()
        .map((u) => ({ ...u }));
      if (sourceUser.canEditEvent) {
        currenValue.push(sourceUser);
        this.allPermittedUsers$.next(currenValue);
      } else {
        this.allPermittedUsers$.next(
          currenValue.filter((u) => u.id !== sourceUser.id)
        );
      }
      this.permittedUsersSearchResults$.next(
        this.permittedUsersSearchResults$.getValue()
      );
    }

    // this.localStorageActions("save", this.allEventData);
  }

  public async searchParentEvents(
    params:
      | (({ code: number } | { name: string }) & {
          limitToCurrentYear: boolean | string;
        })
      | null
  ) {
    try {
      // @ts-ignore
      const text = params.name || params.code;

      const djangoResponse = await observableToPromise(
        this.http.get("django-api/events/", {
          params: {
            search: text,
            page: 1,
            limitToCurrentYear: !!params.limitToCurrentYear,
          },
        })
      );
      const bodyDjango = await djangoResponse.json();

      const body = bodyDjango;

      const serializedResult = EventsService.serializeParentEventsFromServer(
        bodyDjango.results
      );

      this.allParentEvents$.next(serializedResult);

      this.parentEventsSearchState$.next({
        next: body.next,
        count: body.count,
        previous: body.previous,
        isLoading: false,
      });
    } catch (err) {
      this.httpCatch(err);
    }
  }
  public async scrolledToBottomOfResults() {
    const searchState = this.parentEventsSearchState$.getValue();
    if (!searchState) {
      return;
    }
    const allParentEvents = this.allParentEvents$.getValue();
    if (
      searchState.isLoading ||
      !searchState.next ||
      allParentEvents.length > this.MAX_NUMBER_OF_PARENT_EVENTS_ON_SCROLL
    ) {
      return;
    }
    try {
      this.parentEventsSearchState$.next({ ...searchState, isLoading: true });
      // scrolling
      const addressParams = searchState.next.split(`events/?`)[1];
      const response = await observableToPromise(
        this.http.get(`django-api/events/?${addressParams}`)
      );

      const body = await response.json();
      let serializedResult = EventsService.serializeParentEventsFromServer(
        body.results
      );

      const existingParentEvents = allParentEvents.map((parentEvent) => ({
        ...parentEvent,
      }));
      const allExistingParentEventsIds = existingParentEvents.map(
        (pe) => pe.parentEventId
      );
      const newParentEventsWithoutDuplicates = serializedResult.filter(
        (pe) => !allExistingParentEventsIds.includes(pe.parentEventId)
      );

      existingParentEvents.push(...newParentEventsWithoutDuplicates);

      this.allParentEvents$.next(existingParentEvents);
      this.parentEventsSearchState$.next({
        next: body.next,
        count: body.count,
        previous: body.previous,
        isLoading: false,
      });
    } catch (err) {
      this.httpCatch(err);
    }
  }
  public async searchPermittedUsers(params: { search: string }) {
    try {
      const response = await observableToPromise(
        this.http.get("/django-api/users/direct-permission-users/", {
          params,
        })
      );

      const body = await response.json();
      if (!body.results) {
        return;
      }

      const permittedUsersSearchResults: EventEditor[] = body.results.map(
        (responseUser) => {
          const user: EventEditor = {
            canEditEvent: false,
            firstName: responseUser.first_name,
            id: responseUser.id,
            lastName: responseUser.last_name,
            userName: responseUser.email,
          };
          return user;
        }
      );
      this.permittedUsersSearchResults$.next(permittedUsersSearchResults);
    } catch (err) {
      this.httpCatch(err);
    }
  }
  public updateParentQuestion(
    parentQuestion: ParentQuestion,
    options?: {
      delete?: true;
      duplicate?: true;
      move?: -1 | 1;
      updateState?: true;
    }
  ): void {
    const allParentQuestions = this.allQuestions$.getValue();

    if (options && options.delete === true) {
      const parentQuestionsAfterDelete = this.allQuestions$
        .getValue()
        .filter((q) => q.id !== parentQuestion.id);

      setTimeout(() => {
        this.allQuestions$.next(parentQuestionsAfterDelete);
      }, 1);
    } else if (options && options.duplicate === true) {
      const questionToDuplicate: ParentQuestion = allParentQuestions.find(
        (f) => f.id === parentQuestion.id
      );

      const indexOfQuestion = allParentQuestions.indexOf(questionToDuplicate);
      const duplicatePArentQuestion: ParentQuestion = {
        ...questionToDuplicate,
        id: questionToDuplicate.id + 1,
        title: questionToDuplicate.title + " (עותק)",
        options: questionToDuplicate.options
          ? questionToDuplicate.options.map((o) => ({ ...o }))
          : undefined,
      };

      const newParentQuestion = allParentQuestions.map((e) => ({
        ...e,
      }));
      newParentQuestion.splice(indexOfQuestion, 0, duplicatePArentQuestion);

      setTimeout(() => {
        this.allQuestions$.next(newParentQuestion);
      }, 1);
      return;
    } else if (options && options.move) {
      const questionToMove: ParentQuestion = allParentQuestions.find(
        (f) => f.id === parentQuestion.id
      );
      let index = allParentQuestions.indexOf(questionToMove);
      if (
        index + options.move < 0 ||
        index + options.move > allParentQuestions.length - 1
      ) {
        return;
      }
      {
        this.arrayMove(allParentQuestions, index, index + options.move);
        this.allQuestions$.next(allParentQuestions);
      }
    } else {
      const destEvent = allParentQuestions.find(
        (e) => e.id === parentQuestion.id
      );
      this.updateObject(parentQuestion, destEvent);
      destEvent.options = (parentQuestion.options || []).map((o) => ({ ...o }));
    }
  }

  public addNewQuestion() {
    const currentQuestions = this.allQuestions$
      .getValue()
      .map((o) => ({ ...o }));
    const newId = EventSerializerService.createNewQuestionId();
    const newQuestion: ParentQuestion = {
      answer: "",
      id: newId.toString(),
      isRequired: false,
      options: undefined,
      order: 0,
      questionsType: ParentQuestionType.Text,
      subTitle: "",
      title: "",
    };
    currentQuestions.push(newQuestion);
    this.allQuestions$.next(currentQuestions);
  }

  public updateObject<T>(source: T, dest: T): void {
    // tslint:disable-next-line:forin
    for (const prop in source) {
      dest[prop] = source[prop];
    }
  }

  public getChildEventsFromParentEvent(
    parentEvent: ParentEventFromSearch
  ): Observable<ChildEvent[]> {
    return new Observable<ChildEvent[]>((observer) => {
      // this.getChildEventsOfOneParentEvent(parentEvent.parentEventCode).then(
      //   (childEvents) => {
      //     observer.next(childEvents);
      //   }
      // );

      observer.next([]);
    });
  }

  public localStorageActions(
    action: "load" | "save",
    data?: any
  ): AllEvnetData | null {
    const lsKey = eventDemoDataKeyForLS;
    switch (action) {
      case "load":
        const json = localStorage.getItem(lsKey);
        if (json) {
          return JSON.parse(json);
        }
        return null;
      case "save":
        localStorage.setItem(lsKey, JSON.stringify(data));
        return null;
    }
  }

  public resetAllEditDataData(): void {
    this.eventFormInitialData$.next(null);
    this.allQuestions$.next([]);
    this.allPermittedUsers$.next([]);
  }

  private arrayMove(arr, fromIndex, toIndex) {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/eventsServices/events.service.ts